<script setup>
import { ref, computed, onMounted } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import AppHeader from './components/AppHeader.vue'
import AppFooter from './components/AppFooter.vue'
import CookieConsent from './components/CookieConsent.vue'
import BackgroundImage from './components/BackgroundImage.vue'
import AnalyticsInjector from './components/AnalyticsInjector.vue'
import axios from 'axios'
import { useCurrencyStore } from './stores/currencyStore'

const route = useRoute()
const isAdminRoute = computed(() => route.path.startsWith('/admin'))
const appReady = ref(false)
const fadeOut = ref(false)

onMounted(async () => {
  if (isAdminRoute.value) {
    appReady.value = true
    return
  }
  try {
    const currencyStore = useCurrencyStore()
    const API_URL = import.meta.env.VITE_API_URL
    const preloadImage = (src) => new Promise(resolve => {
      const img = new Image()
      img.onload = resolve
      img.onerror = resolve
      img.src = src
    })
    await Promise.allSettled([
      currencyStore.fetchCurrencies(),
      preloadImage('/title-background.jpg'),
      preloadImage('/market-bg.jpg'),
      preloadImage('/profile-bg.jpg'),
      (async () => {
        const token = localStorage.getItem('steam_token')
        if (token) {
          await axios.get(`${API_URL}/api/auth/user`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        }
      })()
    ])
  } catch (e) {
    // continue regardless
  }
  fadeOut.value = true
  setTimeout(() => { appReady.value = true }, 500)
})
</script>

<template>
  <!-- Global Loading Screen -->
  <Transition name="loader-fade">
    <div v-if="!appReady && !isAdminRoute" class="global-loader" :class="{ 'fade-out': fadeOut }">
      <div class="loader-content">
        <div class="loader-bar">
          <div class="loader-bar-fill"></div>
        </div>
      </div>
    </div>
  </Transition>

  <template v-if="!isAdminRoute">
    <BackgroundImage />
    <AppHeader />
  </template>
  <RouterView />
  <AppFooter v-if="!isAdminRoute" />
  <CookieConsent v-if="!isAdminRoute" />
  <AnalyticsInjector v-if="!isAdminRoute" />
</template>

<style>
/* Global styles imported in main.css */
</style>

<style scoped>
.global-loader {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: #060a10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.5s ease;
}

.global-loader.fade-out {
  opacity: 0;
  pointer-events: none;
}

.loader-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loader-bar {
  width: 120px;
  height: 3px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 3px;
  overflow: hidden;
}

.loader-bar-fill {
  height: 100%;
  width: 40%;
  background: linear-gradient(90deg, transparent, #00d4ff, transparent);
  border-radius: 3px;
  animation: loader-slide 1.2s ease-in-out infinite;
}

@keyframes loader-slide {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
}
</style>
